import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { confirmacionesAPI } from "@/lib/api";
import { Plus, Search, Eye, Edit, Trash2, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function ConfirmacionesPage() {
  const [confirmaciones, setConfirmaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadConfirmaciones();
  }, []);

  const loadConfirmaciones = async (searchTerm = "") => {
    try {
      setLoading(true);
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await confirmacionesAPI.getAll(params);
      setConfirmaciones(response.data);
    } catch (error) {
      toast.error("Error al cargar las confirmaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadConfirmaciones(search);
  };

  const handleDelete = async () => {
    try {
      await confirmacionesAPI.delete(deleteId);
      toast.success("Confirmación eliminada correctamente");
      loadConfirmaciones();
    } catch (error) {
      toast.error("Error al eliminar la confirmación");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="confirmaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Flame className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            Confirmaciones
          </h1>
          <p className="text-stone-600 mt-1">Gestión de registros de confirmaciones</p>
        </div>
        <Link to="/confirmaciones/nuevo">
          <Button className="bg-rose-950 hover:bg-rose-900 text-white" data-testid="nueva-confirmacion-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Confirmación
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Buscar por nombre, apellido o número de partida..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-white border-stone-200"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="h-11">
              Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-stone-800">
            {confirmaciones.length} registros encontrados
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-400 animate-pulse">
              Cargando...
            </div>
          ) : confirmaciones.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Flame className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No se encontraron confirmaciones</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-ledger">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-6 py-4">Partida</TableHead>
                    <TableHead className="px-6 py-4">Confirmado</TableHead>
                    <TableHead className="px-6 py-4">Fecha</TableHead>
                    <TableHead className="px-6 py-4">Obispo</TableHead>
                    <TableHead className="px-6 py-4 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {confirmaciones.map((conf) => (
                    <TableRow key={conf.id}>
                      <TableCell className="px-6 py-4 font-medium">
                        {conf.numero_libro}-{conf.numero_folio}-{conf.numero_partida}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {conf.confirmado_nombre} {conf.confirmado_apellido}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {conf.fecha_confirmacion}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {conf.obispo_nombre}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/confirmaciones/${conf.id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={`/confirmaciones/${conf.id}/editar`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(conf.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta confirmación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
